import React, { createContext, useContext, useState, ReactNode, useEffect } from "react"
import { defaultAddTextStep, defaultTextSize } from "../constants/variables"
import { ThemeContext } from "./ThemeContext"

// (0 = Standard, 1 = erste Steigerung, 2 = zweite Steigerung)
type CycleState = 0 | 1 | 2

// Alle Barrierefreiheits-Einstellungen
type AccessibilitySettings = {
    cursorSize: CycleState
    cursorPointerSize: CycleState
    textSpacing: CycleState
}

// Type für Context inklusive der Zustände und Funktionen zum Umschalten
type AccessibilityContextType = {
    settings: AccessibilitySettings
    displayedTextSizeLevel: number
    easySpeech: boolean
    contrast: boolean
    readText: boolean
    cycleTextread: () => void
    setContrast: (e: boolean) => void
    cycleTextSize: () => void
    cycleCursorSize: () => void
    cycleTextSpacing: () => void
    cycleEasySpeech: () => void
    getCursorClass: () => string
    getCursorPointerClass: () => string
    getTextSpacingClass: () => string
}

// Erstelle den Context (initial undefined – Verwendung erfolgt über den Provider)
const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined)

// Hilfsfunktion, die einen Wert im 3‑Stufen‑Cycle aktualisiert: 0 → 1 → 2 → 0
const cycleValue = (value: CycleState): CycleState => ((value + 1) % 3) as CycleState

const cursorSizeMapping: string[] = [
    "cursor-base", // Standard-Cursor
    "cursor-lg", // größerer Cursor
    "cursor-xl", // noch größerer Cursor
]

const cursorPointerSizeMapping: string[] = [
    "cursor-pointer-base", // Standard-Cursor
    "cursor-pointer-lg", // größerer Cursor
    "cursor-pointer-xl", // noch größerer Cursor
]

const textSpacingMapping: string[] = [
    "!tracking-normal", // Standard Textabstand
    "!tracking-wide", // erweiterter Abstand
    "!tracking-wider", // noch erweiterter Abstand
]

// Provider-Komponente, die den Context und alle Umschaltfunktionen bereitstellt
export const AccessibilityProvider = ({ children }: { children: ReactNode }) => {
    // const
    const [easySpeech, setEasySpeech] = useState<boolean>(false)
    const [settings, setSettings] = useState<AccessibilitySettings>({
        cursorSize: 0,
        cursorPointerSize: 0,
        textSpacing: 0,
    })

    // Manipulate Contrast based on accessibilty modal
    const [contrast, setContrast] = useState<boolean>(false)
    const [readText, setReadText] = useState<boolean>(false)
    const color = useContext(ThemeContext)
    useEffect(() => {
        if (contrast) {
            color?.setcolor({
                text: "#050505",
                button: "#3d3b3b",
                bg1: "#f7f7f7",
                bg2: "#f7f7f7",
                bg3: "#f7f7f7",
            })
            color?.setheadlineColor("#050505")
            color?.settextColor("#050505")
        }
    }, [contrast])

    const cycleCursorSize = () => {
        setSettings((prev) => ({ ...prev, cursorSize: cycleValue(prev.cursorSize) }))
        setSettings((prev) => ({ ...prev, cursorPointerSize: cycleValue(prev.cursorPointerSize) }))
    }

    const cycleTextSpacing = () => {
        setSettings((prev) => ({ ...prev, textSpacing: cycleValue(prev.textSpacing) }))
    }

    // Controller to Set/Unset EasySpeech to the Page
    const cycleEasySpeech = () => {
        setEasySpeech(!easySpeech)
    }

    const getCursorClass = (): string => {
        return cursorSizeMapping[settings.cursorSize]
    }
    const getCursorPointerClass = (): string => {
        return cursorPointerSizeMapping[settings.cursorPointerSize]
    }

    const getTextSpacingClass = (): string => {
        return textSpacingMapping[settings.textSpacing]
    }
    const cycleTextread = () => {
        setReadText(!readText)
    }

    // Text Size
    const [currentTextSizeLevel, setCurrentTextSizeLevel] = useState<number>(1)
    const [displayedTextSizeLevel, setDisplayedTextSizeLevel] = useState<number>(1)

    const cycleTextSize = () => {
        Object.entries(defaultTextSize).forEach(([key, value]) => {
            document.documentElement.style.setProperty(key, `${value + defaultAddTextStep * currentTextSizeLevel}rem`)
        })

        // Cycle between 1 → 2 → 3 → back to 1
        setCurrentTextSizeLevel(currentTextSizeLevel < 2 ? currentTextSizeLevel + 1 : 0)
        setDisplayedTextSizeLevel(currentTextSizeLevel !== displayedTextSizeLevel ? currentTextSizeLevel + 1 : displayedTextSizeLevel + 1)
    }

    return (
        <AccessibilityContext.Provider
            value={{
                settings,
                displayedTextSizeLevel,
                easySpeech,
                contrast,
                setContrast,
                cycleTextSize,
                cycleCursorSize,
                readText,
                cycleTextread,
                cycleTextSpacing,
                cycleEasySpeech,
                getCursorClass,
                getCursorPointerClass,
                getTextSpacingClass,
            }}>
            {children}
        </AccessibilityContext.Provider>
    )
}

// Custom Hook zum einfachen Zugriff auf den Accessibility Context in Deinen Komponenten
export const useAccessibility = (): AccessibilityContextType => {
    const context = useContext(AccessibilityContext)
    if (!context) {
        throw new Error("useAccessibility must be used within an AccessibilityProvider")
    }
    return context
}
